// ============================================================
// Le générateur pseudo-aléatoire de Java, rejoué à l'identique
// ============================================================
// Repris de l'aperçu de NG Java (NGConfigAPI, frontend/src/lib/oregen/javaRandom.js), lui-même miroir de
// fr.nationsglory.worldedit.oregen.OreRandom (NGWorldEdit) ; WorldOriginMod (Ores/JavaRandom.cs) rejoue
// le même. L'aperçu n'a d'intérêt que s'il montre EXACTEMENT ce que le mod posera : java.util.Random
// est un générateur congruentiel sur 48 bits, entièrement spécifié, qui se rejoue sans approximation à
// condition de calculer pour de vrai sur 48 et 64 bits. D'où les BigInt : des Number perdraient la
// parité au-delà de 2^53, sans rien signaler, sur certains chunks seulement.
// ============================================================

const MASK_48 = (1n << 48n) - 1n;
const MASK_64 = (1n << 64n) - 1n;
const MULTIPLIER = 0x5deece66dn;
const ADDEND = 0xbn;

/** Reproduit java.util.Random. */
export class JavaRandom {
  private seed = 0n;

  constructor(seed: bigint) {
    this.setSeed(seed);
  }

  setSeed(seed: bigint): void {
    this.seed = (BigInt.asUintN(64, seed) ^ MULTIPLIER) & MASK_48;
  }

  /** next(bits) : les `bits` de poids fort de la graine suivante, comme un entier signé 32 bits. */
  next(bits: number): number {
    this.seed = (this.seed * MULTIPLIER + ADDEND) & MASK_48;
    return Number(BigInt.asIntN(32, this.seed >> BigInt(48 - bits)));
  }

  /**
   * nextInt(bound), boucle de rejet comprise. Le rejet repose sur un débordement d'entier signé 32
   * bits, voulu dans le code de Java : `| 0` reproduit cette troncature.
   */
  nextInt(bound: number): number {
    if ((bound & -bound) === bound) {
      return Number((BigInt(bound) * BigInt(this.next(31))) >> 31n);
    }
    let bits: number;
    let val: number;
    do {
      bits = this.next(31);
      val = bits % bound;
    } while (((bits - val + (bound - 1)) | 0) < 0);
    return val;
  }

  /** nextFloat() : k / 2^24, exactement représentable en float comme en double. */
  nextFloat(): number {
    return this.next(24) / 16777216;
  }
}

/** Finaliseur splitmix64 : deux graines voisines en sortent décorrélées. */
export function mix(value: bigint): bigint {
  let z = (BigInt.asUintN(64, value) + 0x9e3779b97f4a7c15n) & MASK_64;
  z = ((z ^ (z >> 30n)) * 0xbf58476d1ce4e5b9n) & MASK_64;
  z = ((z ^ (z >> 27n)) * 0x94d049bb133111ebn) & MASK_64;
  return (z ^ (z >> 31n)) & MASK_64;
}

/** Reproduit `((long) a << 32) ^ (b & 0xFFFFFFFFL)`, signes compris. */
function packPair(a: number, b: number): bigint {
  const high = (BigInt.asUintN(64, BigInt(a)) << 32n) & MASK_64;
  const low = BigInt(b) & 0xffffffffn;
  return (high ^ low) & MASK_64;
}

/** Graine propre à un chunk, pour une graine de monde donnée. */
export function chunkSeed(worldSeed: bigint, chunkX: number, chunkZ: number): bigint {
  return mix(BigInt.asUintN(64, worldSeed) ^ mix(packPair(chunkX, chunkZ)));
}

/** Graine propre à une ligne de minerai dans un chunk, dérivée des RANGS (couche, ligne). */
export function oreSeed(chunkSeedValue: bigint, layerIndex: number, oreIndex: number): bigint {
  return mix(BigInt.asUintN(64, chunkSeedValue) ^ mix(packPair(layerIndex, oreIndex)));
}
